import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  HStack,
  Icon,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiAlertTriangle } from "react-icons/fi";

const CONFIRMATION_PHRASE = "Yes i confirm";
const CONFIRMATION_INPUT_ID =
  "complete-part-conversion-price-estimate-confirmation";

function normalizeConfirmationText(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

type CompletePartConversionPriceEstimateDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  isConfirmLoading?: boolean;
};

const CompletePartConversionPriceEstimateDialog: React.FC<
  CompletePartConversionPriceEstimateDialogProps
> = ({ isOpen, onClose, onConfirm, isConfirmLoading = false }) => {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [confirmationText, setConfirmationText] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setConfirmationText("");
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isConfirmLoading) return;
    onClose();
  };

  // Quotes around the phrase in the hint are display-only; match typed text only.
  const isConfirmEnabled =
    normalizeConfirmationText(confirmationText) ===
      normalizeConfirmationText(CONFIRMATION_PHRASE) && !isConfirmLoading;

  const handleConfirm = () => {
    if (!isConfirmEnabled) return;
    void onConfirm();
  };

  return (
    <AlertDialog
      leastDestructiveRef={cancelRef}
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size="md"
      closeOnEsc={!isConfirmLoading}
      closeOnOverlayClick={!isConfirmLoading}
    >
      <AlertDialogOverlay bg="blackAlpha.500">
        <AlertDialogContent
          mx={4}
          borderRadius="xl"
          boxShadow="xl"
          overflow="hidden"
        >
          <AlertDialogHeader
            fontSize="lg"
            fontWeight="semibold"
            color="gray.900"
            pb={2}
            pt={6}
            px={6}
          >
            Complete Part Conversion
          </AlertDialogHeader>

          <AlertDialogBody px={6} pb={2}>
            <HStack align="flex-start" spacing={3}>
              <Box
                bg="orange.50"
                borderRadius="full"
                p={2}
                flexShrink={0}
                mt={0.5}
              >
                <Icon as={FiAlertTriangle} color="orange.500" boxSize={5} />
              </Box>
              <VStack align="stretch" spacing={3} flex={1}>
                <Text color="gray.800" lineHeight="tall" fontSize="md">
                  Completing part conversion will share the draft estimate with the customer.
                </Text>
                <Text
                  color="gray.800"
                  lineHeight="tall"
                  fontSize="md"
                  fontWeight="bold"
                >
                  Are you sure you want to continue?
                </Text>
                <VStack align="stretch" spacing={1.5}>
                  <Text
                    as="label"
                    htmlFor={CONFIRMATION_INPUT_ID}
                    color="gray.600"
                    fontSize="sm"
                    lineHeight="tall"
                  >
                    Type{" "}
                    <Text as="span" fontWeight="bold" color="gray.800">
                      "{CONFIRMATION_PHRASE}"
                    </Text>{" "}
                    to continue.
                  </Text>
                  <Input
                    id={CONFIRMATION_INPUT_ID}
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    placeholder={CONFIRMATION_PHRASE}
                    aria-label={`Type ${CONFIRMATION_PHRASE} to continue`}
                    isDisabled={isConfirmLoading}
                    autoComplete="off"
                    size="md"
                  />
                </VStack>
              </VStack>
            </HStack>
          </AlertDialogBody>

          <AlertDialogFooter px={6} pt={5} pb={6}>
            <Button
              ref={cancelRef}
              onClick={handleClose}
              variant="outline"
              borderColor="gray.300"
              color="gray.700"
              isDisabled={isConfirmLoading}
              _hover={{ bg: "gray.50" }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              bg="orange.500"
              ml={3}
              onClick={handleConfirm}
              isLoading={isConfirmLoading}
              isDisabled={!isConfirmEnabled}
              _hover={{ bg: "orange.600" }}
            >
              Confirm
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default CompletePartConversionPriceEstimateDialog;
